import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Camera, X, Plus, Upload, ArrowLeft, ArrowRight, Sparkles, MapPin, Truck, Hand, Loader2, WalletCards, Pencil } from "lucide-react";
import { api, imgSrc } from "@/lib/api";
import { track } from "@/lib/analytics";
import { toast } from "sonner";
import { TRADING_CARDS_CATEGORY, CARD_GAME_GROUPS, CARD_TYPES, GRADING_COMPANIES, GRADES, CARD_CONDITIONS } from "@/lib/cards";
import ImageEditorModal from "@/components/ImageEditorModal";

const CATEGORIES = [TRADING_CARDS_CATEGORY, "Cameras", "Phones", "Computers", "Tablets", "Gaming", "Music", "Clothing", "Home", "Furniture", "Sports", "Electronics", "Books", "Jewelry & Watches", "Other"];
const CONDITIONS = [
  { value: "new", label: "New", desc: "Brand new, unused" },
  { value: "like_new", label: "Like new", desc: "Barely used, mint condition" },
  { value: "good", label: "Good", desc: "Used with minor wear" },
  { value: "fair", label: "Fair", desc: "Visible wear but functional" },
];
const DELIVERY = [
  { value: "local", label: "Local pickup", icon: Hand, desc: "Meet up in person nearby" },
  { value: "shipping", label: "Shipping", icon: Truck, desc: "I'll ship it" },
  { value: "both", label: "Both", icon: ArrowRight, desc: "Pickup or shipping" },
];

export default function PostTrade() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [images, setImages] = useState([]); // [{path, url}]
  const [uploading, setUploading] = useState(false);
  const [editorIdx, setEditorIdx] = useState(null);
  const [editorUrl, setEditorUrl] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [locating, setLocating] = useState(false);
  const [coords, setCoords] = useState({ lat: null, lng: null });
  const [form, setForm] = useState({
    title: "",
    category: "",
    condition: "good",
    description: "",
    location: "",
    visibility: "nationwide",
    delivery: "both",
    wants: [""],
    open_to_offers: true,
    estimated_value: "",
    card: { game: "", card_type: "single", graded: false, grading_company: "PSA", grade: "10", set_name: "", year: "", card_condition: "near_mint" },
  });

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const setCard = (k, v) => setForm(f => ({ ...f, card: { ...f.card, [k]: v } }));
  const isCards = form.category === TRADING_CARDS_CATEGORY;

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    const newImgs = [];
    for (const file of files) {
      const fd = new FormData();
      fd.append("file", file);
      try {
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        newImgs.push({ path: data.path, url: data.url });
      } catch (err) {
        toast.error(`Failed to upload ${file.name}`);
      }
    }
    setImages(prev => [...prev, ...newImgs]);
    setUploading(false);
    e.target.value = "";
  };

  const removeImage = (idx) => setImages(prev => prev.filter((_, i) => i !== idx));

  const makeCover = (idx) => setImages(prev => idx === 0 ? prev : [prev[idx], ...prev.filter((_, i) => i !== idx)]);

  const openEditor = async (idx) => {
    const img = images[idx];
    if (!img) return;
    try {
      const res = await fetch(imgSrc(img.url), { credentials: "omit" });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      setEditorUrl(url);
      setEditorIdx(idx);
    } catch {
      toast.error("Could not open image for editing");
    }
  };

  const closeEditor = () => {
    if (editorUrl) URL.revokeObjectURL(editorUrl);
    setEditorUrl(null);
    setEditorIdx(null);
  };

  const saveEditor = async (blob) => {
    if (editorIdx == null) return;
    try {
      const fd = new FormData();
      fd.append("file", blob, `edited-${Date.now()}.jpg`);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImages(prev => prev.map((im, i) => i === editorIdx ? { path: data.path, url: data.url } : im));
      toast.success("Image updated");
      closeEditor();
    } catch {
      toast.error("Couldn't save the edited image");
    }
  };

  const analyzeWithAI = async () => {
    if (images.length === 0) { toast.error("Add a photo first"); return; }
    setAnalyzing(true);
    try {
      const { data } = await api.post("/listings/analyze-photos", {
        image_paths: images.slice(0, 3).map(i => i.path),
      });
      setForm(f => ({
        ...f,
        title: data.title || f.title,
        category: data.category || f.category,
        condition: data.condition || f.condition,
        description: data.description || f.description,
      }));
      toast.success("Auto-filled from photos — review before posting");
      // Jump to step 2 if currently on step 1
      if (step === 1) setStep(2);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not analyze photos");
    } finally { setAnalyzing(false); }
  };

  const useMyLocation = () => {
    if (!navigator.geolocation) { toast.error("Geolocation not supported by your browser"); return; }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(async (pos) => {
      try {
        const { latitude, longitude } = pos.coords;
        const { data } = await api.post("/geo/reverse", { lat: latitude, lng: longitude });
        setCoords({ lat: latitude, lng: longitude });
        set("location", data.formatted || `${latitude.toFixed(2)}, ${longitude.toFixed(2)}`);
        toast.success("Got your location");
      } catch (e) { toast.error("Could not reverse geocode"); }
      finally { setLocating(false); }
    }, (err) => {
      setLocating(false);
      toast.error(err.code === 1 ? "Permission denied" : "Could not get location");
    }, { enableHighAccuracy: false, timeout: 10000 });
  };

  const setWant = (i, v) => setForm(f => ({ ...f, wants: f.wants.map((w, idx) => idx === i ? v : w) }));
  const addWant = () => setForm(f => ({ ...f, wants: [...f.wants, ""] }));
  const removeWant = (i) => setForm(f => ({ ...f, wants: f.wants.filter((_, idx) => idx !== i) }));

  const canNext = () => {
    if (step === 1) return images.length > 0;
    if (step === 2) return form.title.trim() && form.category && form.description.trim() && form.location.trim() && (!isCards || form.card.game);
    if (step === 3) return true;
    return true;
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const ev = form.estimated_value ? parseInt(form.estimated_value, 10) : null;
      const { card, ...rest } = form;
      const card_details = isCards ? {
        game: card.game,
        card_type: card.card_type,
        graded: card.graded,
        ...(card.graded
          ? { grading_company: card.grading_company, grade: card.grade }
          : { card_condition: card.card_condition }),
        set_name: card.set_name.trim(),
        year: card.year.trim(),
      } : null;
      const payload = {
        ...rest,
        card_details,
        wants: form.wants.map(w => w.trim()).filter(Boolean),
        images: images.map(i => i.url),
        lat: coords.lat,
        lng: coords.lng,
        estimated_value: Number.isFinite(ev) && ev > 0 ? ev : null,
      };
      const { data } = await api.post("/listings", payload);
      track("create_listing", { category: payload.category });
      toast.success("Listing posted!");
      navigate(`/what-can-i-get?item_id=${data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not post listing");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="section-pad py-6 md:py-12 max-w-3xl mx-auto" data-testid="post-trade-page">
      <h1 className="font-display font-bold text-3xl md:text-4xl tracking-tight">Post a trade</h1>
      <p className="text-zinc-500 mt-2 text-sm md:text-base">Show us what you have. We’ll show you what it can unlock.</p>

      {/* Stepper */}
      <div className="mt-6 flex items-center gap-2">
        {[1, 2, 3].map(n => (
          <div key={n} className={`flex-1 h-1 rounded-full transition-colors ${step >= n ? "bg-[#0654BA]" : "bg-[#F1F5F9]"}`} />
        ))}
      </div>
      <p className="text-xs text-zinc-500 mt-2">Step {step} of 3</p>

      <div className="mt-6 vyrti-card p-5 md:p-8">
        {step === 1 && (
          <div data-testid="step-photos">
            <h2 className="font-display font-semibold text-xl md:text-2xl">Add photos</h2>
            <p className="text-sm text-zinc-500 mt-1">Up to 8 photos — tap "Set cover" to pick your cover photo.</p>

            <div className="mt-4 flex items-start gap-2.5 rounded-xl bg-[#E0EDFA] border border-[#0654BA]/15 px-3.5 py-2.5" data-testid="edit-photo-hint">
              <Pencil className="w-4 h-4 text-[#0654BA] mt-0.5 shrink-0" />
              <p className="text-xs text-[#054a9e] leading-relaxed">
                <strong className="font-semibold">Tip:</strong> tap the pencil icon on any photo to rotate, flip, crop, or tweak brightness before you post.
              </p>
            </div>

            <div className="mt-5 grid grid-cols-3 sm:grid-cols-4 gap-3">
              {images.map((img, i) => (
                <div key={img.path || img.url} className="relative aspect-square rounded-2xl overflow-hidden bg-[#E2E2E2] group">
                  <img src={imgSrc(img.url)} alt="" className="w-full h-full object-cover" />
                  <button onClick={() => removeImage(i)} className="absolute top-1.5 right-1.5 w-7 h-7 rounded-full bg-black/60 text-white flex items-center justify-center md:opacity-0 md:group-hover:opacity-100 opacity-100 transition" data-testid={`remove-image-${i}`}>
                    <X className="w-4 h-4" />
                  </button>
                  <button onClick={() => openEditor(i)} className="absolute top-1.5 left-1.5 w-7 h-7 rounded-full bg-black/60 text-white flex items-center justify-center md:opacity-0 md:group-hover:opacity-100 opacity-100 transition hover:bg-[#0654BA]" title="Edit image" data-testid={`edit-image-${i}`}>
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                  {i === 0 ? (
                    <span className="absolute bottom-1.5 left-1.5 vyrti-pill !bg-black/60 !text-white text-[10px]">Cover</span>
                  ) : (
                    <button onClick={() => makeCover(i)} className="absolute bottom-1.5 left-1.5 px-2 py-0.5 rounded-full bg-black/60 text-white text-[10px] font-medium hover:bg-[#0654BA] transition-colors" data-testid={`make-cover-${i}`}>
                      Set cover
                    </button>
                  )}
                </div>
              ))}
              {images.length < 8 && (
                <label className="aspect-square rounded-2xl border-2 border-dashed border-zinc-200 flex flex-col items-center justify-center cursor-pointer hover:border-[#0654BA] hover:bg-[#E0EDFA] transition-colors" data-testid="upload-trigger">
                  <input type="file" accept="image/*" multiple onChange={handleUpload} className="hidden" />
                  {uploading ? (
                    <div className="w-6 h-6 rounded-full border-2 border-zinc-200 border-t-[#0654BA] animate-spin" />
                  ) : (
                    <>
                      <Camera className="w-6 h-6 text-zinc-400" />
                      <span className="text-xs text-zinc-500 mt-1">Add photo</span>
                    </>
                  )}
                </label>
              )}
            </div>

            {images.length > 0 && (
              <button
                onClick={analyzeWithAI}
                disabled={analyzing}
                className="mt-5 w-full btn-pill !py-3 !bg-gradient-to-r !from-[#0654BA] !to-[#6366F1] !text-white disabled:opacity-60"
                data-testid="analyze-photos-btn"
              >
                {analyzing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                {analyzing ? "Reading your photos…" : "Auto-fill details from photos"}
              </button>
            )}

            <p className="text-xs text-zinc-400 mt-4 flex items-center gap-2">
              <Upload className="w-3.5 h-3.5" />
              Natural light, clean background, show condition honestly.
            </p>
          </div>
        )}

        {step === 2 && (
          <div data-testid="step-details" className="space-y-5">
            <h2 className="font-display font-semibold text-xl md:text-2xl">Item details</h2>

            {images.length > 0 && !form.title && (
              <button onClick={analyzeWithAI} disabled={analyzing} className="w-full btn-pill btn-soft border border-dashed border-[#0654BA]/40 !text-[#0654BA] !text-sm" data-testid="step2-analyze-btn">
                {analyzing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                {analyzing ? "Reading photos…" : "Auto-fill from photos with AI"}
              </button>
            )}

            <div>
              <label className="block text-sm font-medium mb-1.5">Title</label>
              <input className="input-text" placeholder="e.g. Canon EOS 5D Mark II" value={form.title} onChange={e => set("title", e.target.value)} data-testid="input-title" />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1.5">Category</label>
              <div className="flex flex-wrap gap-2">
                {CATEGORIES.map(c => (
                  <button key={c} type="button" onClick={() => set("category", c)} className={`btn-pill !text-sm ${form.category === c ? "btn-primary" : "btn-soft"}`} data-testid={`cat-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>{c}</button>
                ))}
              </div>
            </div>

            {isCards && (
              <div className="rounded-2xl border-2 border-[#F5AF02]/50 bg-[#FFFBEB] p-4 space-y-4" data-testid="card-details-section">
                <p className="font-semibold text-sm flex items-center gap-2 text-[#92400E]">
                  <WalletCards className="w-4 h-4" /> Card details
                </p>

                <div>
                  <label className="block text-sm font-medium mb-1.5">Game / Sport <span className="text-rose-600">*</span></label>
                  <select className="input-text" value={form.card.game} onChange={e => setCard("game", e.target.value)} data-testid="card-game-select">
                    <option value="">Select…</option>
                    {CARD_GAME_GROUPS.map(g => (
                      <optgroup key={g.group} label={g.group}>
                        {g.items.map(it => <option key={it} value={it}>{it}</option>)}
                      </optgroup>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1.5">What are you trading?</label>
                  <div className="flex flex-wrap gap-2">
                    {CARD_TYPES.map(t => (
                      <button key={t.value} type="button" onClick={() => setCard("card_type", t.value)} className={`btn-pill !text-sm ${form.card.card_type === t.value ? "btn-primary" : "btn-soft !bg-white"}`} data-testid={`card-type-${t.value}`}>{t.label}</button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1.5">Graded or raw?</label>
                  <div className="flex gap-2">
                    <button type="button" onClick={() => setCard("graded", true)} className={`btn-pill !text-sm ${form.card.graded ? "btn-primary" : "btn-soft !bg-white"}`} data-testid="card-graded-yes">Graded</button>
                    <button type="button" onClick={() => setCard("graded", false)} className={`btn-pill !text-sm ${!form.card.graded ? "btn-primary" : "btn-soft !bg-white"}`} data-testid="card-graded-no">Raw / Ungraded</button>
                  </div>
                </div>

                {form.card.graded ? (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium mb-1.5">Grading company</label>
                      <select className="input-text" value={form.card.grading_company} onChange={e => setCard("grading_company", e.target.value)} data-testid="card-grading-company">
                        {GRADING_COMPANIES.map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1.5">Grade</label>
                      <select className="input-text" value={form.card.grade} onChange={e => setCard("grade", e.target.value)} data-testid="card-grade">
                        {GRADES.map(g => <option key={g} value={g}>{g}</option>)}
                      </select>
                    </div>
                  </div>
                ) : (
                  <div>
                    <label className="block text-sm font-medium mb-1.5">Card condition</label>
                    <select className="input-text" value={form.card.card_condition} onChange={e => setCard("card_condition", e.target.value)} data-testid="card-condition">
                      {CARD_CONDITIONS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                    </select>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium mb-1.5">Set <span className="text-zinc-400 font-normal text-xs">(optional)</span></label>
                    <input className="input-text" placeholder="e.g. Base Set, Evolving Skies" value={form.card.set_name} onChange={e => setCard("set_name", e.target.value)} data-testid="card-set-name" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1.5">Year <span className="text-zinc-400 font-normal text-xs">(optional)</span></label>
                    <input className="input-text" placeholder="e.g. 1999" inputMode="numeric" value={form.card.year} onChange={e => setCard("year", e.target.value.replace(/[^0-9]/g, "").slice(0, 4))} data-testid="card-year" />
                  </div>
                </div>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium mb-1.5">Condition</label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {CONDITIONS.map(c => (
                  <button key={c.value} type="button" onClick={() => set("condition", c.value)} className={`rounded-2xl border-2 px-3 py-3 text-left transition-all ${form.condition === c.value ? "border-[#0654BA] bg-[#E0EDFA]" : "border-[#E2E2E2] hover:border-zinc-200"}`} data-testid={`condition-${c.value}`}>
                    <p className="font-medium text-sm">{c.label}</p>
                    <p className="text-[11px] text-zinc-500 mt-0.5">{c.desc}</p>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1.5">Description</label>
              <textarea className="input-text min-h-[120px]" placeholder="Describe condition, what's included, any quirks…" value={form.description} onChange={e => set("description", e.target.value)} data-testid="input-description" />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1.5">Private value check <span className="text-zinc-400 font-normal text-xs">(optional)</span></label>
              <div className="relative max-w-xs">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 text-sm font-medium">$</span>
                <input
                  type="number"
                  min="1"
                  inputMode="numeric"
                  className="input-text !pl-7"
                  placeholder="e.g. 500"
                  value={form.estimated_value}
                  onChange={e => set("estimated_value", e.target.value.replace(/[^0-9]/g, ""))}
                  data-testid="input-estimated-value"
                />
              </div>
              <p className="text-[11px] text-zinc-500 mt-1">Only Vyrti uses this to balance matches. Other traders never see the amount.</p>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1.5">Location</label>
                <div className="flex gap-2">
                  <input className="input-text flex-1" placeholder="City, State" value={form.location} onChange={e => { set("location", e.target.value); setCoords({ lat: null, lng: null }); }} data-testid="input-location" />
                  <button type="button" onClick={useMyLocation} disabled={locating} className="btn-pill btn-soft !px-3 flex-shrink-0" title="Use my current location" data-testid="use-my-location">
                    {locating ? <Loader2 className="w-4 h-4 animate-spin" /> : <MapPin className="w-4 h-4" />}
                  </button>
                </div>
                {coords.lat !== null && <p className="text-[11px] text-emerald-600 mt-1">📍 Using your current location</p>}
              </div>
              <div>
                <label className="block text-sm font-medium mb-1.5">Visibility</label>
                <select className="input-text" value={form.visibility} onChange={e => set("visibility", e.target.value)} data-testid="input-visibility">
                  <option value="nationwide">Nationwide</option>
                  <option value="local">Local only</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1.5">Delivery</label>
              <div className="grid grid-cols-3 gap-2">
                {DELIVERY.map(d => {
                  const Icon = d.icon;
                  return (
                    <button key={d.value} type="button" onClick={() => set("delivery", d.value)} className={`rounded-2xl border-2 px-3 py-3 text-left transition-all ${form.delivery === d.value ? "border-[#0654BA] bg-[#E0EDFA]" : "border-[#E2E2E2] hover:border-zinc-200"}`} data-testid={`delivery-${d.value}`}>
                      <Icon className={`w-4 h-4 mb-1 ${form.delivery === d.value ? "text-[#0654BA]" : "text-zinc-500"}`} />
                      <p className="font-medium text-sm">{d.label}</p>
                      <p className="text-[11px] text-zinc-500 mt-0.5">{d.desc}</p>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {step === 3 && (
          <div data-testid="step-wants" className="space-y-5">
            <h2 className="font-display font-semibold text-xl md:text-2xl">Anything you’d especially like?</h2>
            <p className="text-sm text-zinc-500">Optional. Preferences improve ranking, but your Match Radar will show every balanced possibility.</p>

            <div className="space-y-2">
              {form.wants.map((w, i) => (
                <div key={`want-slot-${i}`} className="flex items-center gap-2">
                  <input className="input-text" placeholder={`e.g. ${i === 0 ? "iPhone 13" : i === 1 ? "MacBook Air" : "Mechanical keyboard"}`} value={w} onChange={e => setWant(i, e.target.value)} data-testid={`want-input-${i}`} />
                  {form.wants.length > 1 && (
                    <button onClick={() => removeWant(i)} className="p-2 rounded-full hover:bg-[#F1F5F9]" data-testid={`remove-want-${i}`}>
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
              <button type="button" onClick={addWant} className="text-sm text-[#0654BA] hover:underline inline-flex items-center gap-1" data-testid="add-want">
                <Plus className="w-4 h-4" /> Add another item
              </button>
            </div>

            <label className="flex items-start gap-3 cursor-pointer mt-6 vyrti-card !shadow-none border border-[#E2E2E2] p-4 hover:border-zinc-200">
              <input type="checkbox" checked={form.open_to_offers} onChange={e => set("open_to_offers", e.target.checked)} className="mt-1 w-4 h-4 accent-[#0654BA]" data-testid="input-open-offers" />
              <span>
                <p className="font-medium text-sm">Open to other offers</p>
                <p className="text-xs text-zinc-500 mt-0.5">Let users propose trades outside your wishlist. Doubles your match potential.</p>
              </span>
            </label>

            <div className="vyrti-card !shadow-none border border-amber-200 bg-amber-50 p-4 text-xs text-amber-800">
              <p className="font-medium mb-1">⚠️ Vyrti is for tangible items only.</p>
              <p>No services, weapons, adult content, drugs, alcohol, or live animals. Listings are auto-screened.</p>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="mt-8 flex items-center justify-between">
          {step > 1 ? (
            <button onClick={() => setStep(step - 1)} className="btn-pill btn-soft" data-testid="step-back">
              <ArrowLeft className="w-4 h-4" /> Back
            </button>
          ) : <div />}
          {step < 3 ? (
            <button onClick={() => setStep(step + 1)} disabled={!canNext()} className="btn-pill btn-accent disabled:opacity-50 disabled:cursor-not-allowed" data-testid="step-next">
              Continue <ArrowRight className="w-4 h-4" />
            </button>
          ) : (
            <button onClick={submit} disabled={submitting || !canNext()} className="btn-pill btn-accent disabled:opacity-50" data-testid="post-submit">
              {submitting ? "Posting…" : "Post listing"}
            </button>
          )}
        </div>
      </div>

      <ImageEditorModal
        open={editorIdx != null}
        imageUrl={editorUrl}
        onCancel={closeEditor}
        onSave={saveEditor}
      />
    </div>
  );
}
